/**
 * Priority truth contract implementation.
 * Defines the canonical representation of priority snapshots and state.
 */

import type { CanonicalScoreSet } from "../harmonizer/types";

/**
 * A priority snapshot represents a finalized priority state at a specific point in time.
 * 
 * Each snapshot is immutable and contains:
 * - A unique snapshot ID
 * - The session it belongs to
 * - The tool that created it
 * - The timestamp when it was created
 * - The canonical score set with all scores/ranks/categories
 */
export type PrioritySnapshot = {
  snapshotId: string;
  sessionId: string;
  toolName: string;
  createdAt: number;
  scoreSet: CanonicalScoreSet;
};

/**
 * Priority state for a session.
 * 
 * Maintains:
 * - The latest snapshot (current truth)
 * - Complete history of all snapshots
 */
export type PriorityState = {
  latestSnapshot: PrioritySnapshot | null;
  history: PrioritySnapshot[];
};

/**
 * Applies a new snapshot to a priority state.
 * 
 * This is a pure function that:
 * 1. Appends the snapshot to the history
 * 2. Updates latestSnapshot to the new snapshot
 * 
 * The previous state is not mutated; a new state is returned.
 * 
 * @param state - Current priority state
 * @param snapshot - New snapshot to apply
 * @returns New priority state with the snapshot applied
 */
export function applySnapshot(
  state: PriorityState,
  snapshot: PrioritySnapshot
): PriorityState {
  return {
    latestSnapshot: snapshot,
    history: [...state.history, snapshot],
  };
}

/**
 * Creates an initial empty priority state.
 * 
 * @returns Empty priority state with no snapshots
 */
export function createEmptyPriorityState(): PriorityState {
  return {
    latestSnapshot: null,
    history: [],
  };
}


